/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1790640000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue Sep 29 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "snowflake-iceberg",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Prerequisites",
          "depth": 0,
          "id": "prerequisites"
        }, {
          "value": "Snowflake credential setup",
          "depth": 0,
          "id": "snowflake-credential-setup"
        }, {
          "value": "Service account setup",
          "depth": 1,
          "id": "service-account-setup"
        }, {
          "value": "Connect to Snowflake Iceberg",
          "depth": 0,
          "id": "connect-to-snowflake-iceberg"
        }, {
          "value": "Iceberg configuration",
          "depth": 1,
          "id": "iceberg-configuration"
        }, {
          "value": "Credentials",
          "depth": 1,
          "id": "credentials"
        }, {
          "value": "RSA authentication",
          "depth": 1,
          "id": "rsa-authentication"
        }, {
          "value": "SSH tunneling",
          "depth": 1,
          "id": "ssh-tunneling"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Table selection",
          "depth": 1,
          "id": "table-selection"
        }, {
          "value": "Sync mode",
          "depth": 1,
          "id": "sync-mode"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Column types",
          "depth": 1,
          "id": "column-types"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Batch size",
          "depth": 1,
          "id": "batch-size"
        }, {
          "value": "Delete behavior",
          "depth": 1,
          "id": "delete-behavior"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "External volume errors",
          "depth": 1,
          "id": "external-volume-errors"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      p: "p",
      a: "a",
      ul: "ul",
      li: "li",
      code: "code",
      h3: "h3",
      pre: "pre",
      span: "span",
      strong: "strong"
    }, _provideComponents(), props.components), {SnowflakePasswordDeprecation, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SnowflakePasswordDeprecation) _missingMdxReference("SnowflakePasswordDeprecation", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Any data set"
            }), _jsx(_components.td, {
              children: "Sync data from any source to a Snowflake Iceberg table"
            }), _jsx(_components.td, {
              children: "Upsert, Insert, Mirror"
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["This destination is for Snowflake-managed Iceberg tables. If you want to sync to standard Snowflake tables, use the ", _jsx(_components.a, {
            href: "/destinations/snowflake",
            children: "Snowflake destination"
          }), " instead."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "prerequisites",
        children: "Prerequisites"
      }), "\n", _jsxs(_components.p, {
        children: ["Before setting up the Snowflake Iceberg destination, you must have an ", _jsx(_components.a, {
          href: "https://docs.snowflake.com/en/user-guide/tables-iceberg-configure-external-volume",
          children: "external volume"
        }), " configured in Snowflake. Iceberg tables require an external volume that specifies where the table data and metadata are stored."]
      }), "\n", _jsx(_components.h2, {
        id: "snowflake-credential-setup",
        children: "Snowflake credential setup"
      }), "\n", _jsx(_components.p, {
        children: "To allow Hightouch access to Snowflake for Iceberg tables, it's best to create a user specifically provisioned with access to the required tables, schemas, and external volumes. You can also use a personal Snowflake login for your credentials, as long as it has the correct permissions, specifically:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Add, update, and delete (", _jsx(_components.a, {
            href: "#delete-behavior",
            children: "if applicable"
          }), ") rows in your sync's Iceberg table"]
        }), "\n", _jsxs(_components.li, {
          children: ["View the ", _jsx(_components.code, {
            children: "INFORMATION_SCHEMA.COLUMNS"
          }), " table which is used for gathering metadata to set up the sync"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "USAGE"
          }), " privilege on the external volume used for Iceberg tables"]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "service-account-setup",
        children: "Service account setup"
      }), "\n", _jsx(_components.p, {
        children: "You can use the following SQL template to create a service account with the necessary roles and permissions:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-comment",
            children: "-- Edit the following variables"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "set"
          }), " ht_username", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: "'HIGHTOUCH_USER'"
          }), ";\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "set"
          }), " ht_default_warehouse", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: "'<warehouse>'"
          }), ";\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "set"
          }), " ht_database", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: "'<database>'"
          }), ";\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "set"
          }), " ht_default_namespace", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: "'<database.schema>'"
          }), ";\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "set"
          }), " ht_default_role", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: "'HIGHTOUCH_ICEBERG_ROLE'"
          }), ";\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "set"
          }), " ht_external_volume", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: "'<external_volume_name>'"
          }), ";\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "set"
          }), " ht_comment", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: "'Used for Hightouch Iceberg integrations'"
          }), ";\n\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "-- Set role for grants"
          }), "\nUSE ROLE ACCOUNTADMIN;\n\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "-- Create a role for Hightouch"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "CREATE"
          }), " ROLE IF ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXISTS"
          }), " identifier($ht_default_role)\nCOMMENT ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " $ht_comment;\n\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "-- Create Hightouch's user"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "CREATE"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "USER"
          }), " IF ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXISTS"
          }), " identifier($ht_username)\nTYPE", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), "service\nrsa_public_key", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: "'<public_key>'"
          }), "\nDEFAULT_WAREHOUSE", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), "$ht_default_warehouse\nDEFAULT_NAMESPACE", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), "$ht_default_namespace\nDEFAULT_ROLE", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), "$ht_default_role\nCOMMENT", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), "$ht_comment;\n\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "-- Grant permissions to the role"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " ROLE identifier($ht_default_role) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE SYSADMIN;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " USAGE ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " WAREHOUSE identifier($ht_default_warehouse) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE identifier($ht_default_role);\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " ROLE identifier($ht_default_role) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "USER"
          }), " identifier($ht_username);\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " USAGE ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " DATABASE identifier($ht_database) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE identifier($ht_default_role);\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " USAGE ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ALL"
          }), " SCHEMAS ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "IN"
          }), " DATABASE identifier($ht_database) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE identifier($ht_default_role);\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ALL"
          }), " TABLES ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "IN"
          }), " DATABASE identifier($ht_database) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE identifier($ht_default_role);\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " FUTURE TABLES ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "IN"
          }), " DATABASE identifier($ht_database) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE identifier($ht_default_role);\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ALL"
          }), " VIEWS ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "IN"
          }), " DATABASE identifier($ht_database) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE identifier($ht_default_role);\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " FUTURE VIEWS ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "IN"
          }), " DATABASE identifier($ht_database) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE identifier($ht_default_role);\n\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "-- Iceberg-specific permissions"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " USAGE ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXTERNAL"
          }), " VOLUME identifier($ht_external_volume) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE identifier($ht_default_role);\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "CREATE"
          }), " ICEBERG ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TABLE"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " SCHEMA ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "<"
          }), "database.schema", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE identifier($ht_default_role);\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "Once you've created a Snowflake service account, you're ready to set up the connection in Hightouch."
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-snowflake-iceberg",
        children: "Connect to Snowflake Iceberg"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Snowflake Iceberg"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". If you're not using a ", _jsx(_components.a, {
          href: "#ssh-tunneling",
          children: "tunnel"
        }), ", you can then authenticate Hightouch to Snowflake by entering the following fields:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Account identifier"
          }), " - You can find this identifier at the beginning of your Snowflake URL, for example, (", _jsx(_components.code, {
            children: "https://ACCOUNT_IDENTIFIER.snowflakecomputing.com"
          }), ")."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Warehouse"
          }), " - This specifies the warehouse to use when Hightouch executes queries in Snowflake."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Database"
          }), " - This specifies the database to use when Hightouch executes queries in Snowflake."]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "Account identifier"
          }), " format may differ based on Snowflake account age. For example, older Snowflake accounts often have identifiers that look like ", _jsx(_components.code, {
            children: "ACCOUNT_LOCATOR.CLOUD_REGION_ID.CLOUD"
          }), ", whereas newer Snowflake accounts have identifiers that look like ", _jsx(_components.code, {
            children: "ORGNAME-ACCOUNT_NAME"
          }), ".\nFor more details, visit ", _jsx(_components.a, {
            href: "https://docs.snowflake.com/en/user-guide/admin-account-identifier.html",
            children: "Snowflake's account identifier docs"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "iceberg-configuration",
        children: "Iceberg configuration"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "External volume"
          }), " - The Snowflake external volume that provides storage for Iceberg tables. This must be configured in your Snowflake account before setting up the destination."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Base location"
          }), " (optional) - A prefix path within the external volume where Iceberg tables will be stored."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "credentials",
        children: "Credentials"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Username"
          }), " - This can be your personal Snowflake login or a dedicated user for Hightouch. ", _jsx(_components.strong, {
            children: "Note"
          }), ": Use the \"Login name\", which may ", _jsx(_components.a, {
            href: "https://docs.snowflake.com/en/user-guide/admin-user-management#using-classic-console",
            children: "differ from the Snowflake username"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Role"
          }), " (optional) - Use this field to specify the role Hightouch should use when executing queries in Snowflake. If left blank, Hightouch uses the user's default role."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "You have two options for finalizing your credentials:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "RSA key pair (recommended)"
        }), "\n", _jsx(_components.li, {
          children: "Password"
        }), "\n"]
      }), "\n", _jsx(SnowflakePasswordDeprecation, {}), "\n", _jsxs(_components.p, {
        children: ["To ensure your credentials are correct, click ", _jsx(_components.strong, {
          children: "Test connection"
        }), ". This confirms if Hightouch can connect to your database and verify access to the external volume."]
      }), "\n", _jsx(_components.h3, {
        id: "rsa-authentication",
        children: "RSA authentication"
      }), "\n", _jsxs(_components.p, {
        children: ["Generate a ", _jsx(_components.a, {
          href: "https://docs.snowflake.com/en/user-guide/key-pair-auth.html#step-1-generate-the-private-key",
          children: "private key"
        }), " and ", _jsx(_components.a, {
          href: "https://docs.snowflake.com/en/user-guide/key-pair-auth.html#step-2-generate-a-public-key",
          children: "public key"
        }), " by running the following commands in your terminal:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-bash",
          children: ["$ openssl genrsa 2048 | openssl pkcs8 -topk8 -inform PEM -out rsa_key.p8\n$ openssl rsa -", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "in"
          }), " rsa_key.p8 -pubout -out rsa_key.pub\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Save"
        }), " the password you used for the key, you will need to enter it when creating the Snowflake Iceberg destination in Hightouch."]
      }), "\n", _jsxs(_components.p, {
        children: ["Then in Snowflake, execute an ", _jsx(_components.code, {
          children: "ALTER USER"
        }), " command to assign the public key to your Hightouch Snowflake user:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-bash",
          children: ["alter user ht_user ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "set"
          }), " rsa_public_key=", _jsx(_components.span, {
            className: "hljs-string",
            children: "'MIIBIjANBgkqh...'"
          }), ";\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Finally, in Hightouch under the ", _jsx(_components.strong, {
          children: "Authentication Method"
        }), " section, drag and drop or upload the private key you just generated into the ", _jsx(_components.strong, {
          children: "Private key file"
        }), " field. Enter the password for your private key in the box below here, unless you used an unencrypted key."]
      }), "\n", _jsx(_components.h3, {
        id: "ssh-tunneling",
        children: "SSH tunneling"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch can connect directly to Snowflake over the public internet or via an SSH tunnel. Since data is encrypted in transit via TLS, a direct connection is suitable for most use cases. You may need to set up a tunnel if your Snowflake instance is on a private network or virtual private cloud (VPC)."
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports both standard and reverse SSH tunnels. To learn more about SSH tunneling, refer to Hightouch's ", _jsx(_components.a, {
          href: "/security/ssh-tunneling",
          children: "tunneling documentation"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Snowflake Iceberg destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Snowflake Iceberg destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "table-selection",
        children: "Table selection"
      }), "\n", _jsxs(_components.p, {
        children: ["Select the schema and Iceberg table you want to sync to. You can either select an existing Iceberg table or enable ", _jsx(_components.strong, {
          children: "Automatically create and update Iceberg table"
        }), " to have Hightouch create a new Iceberg table using your configured external volume. If auto-create is enabled and the table already exists, Hightouch alters its definition to add new columns as needed."]
      }), "\n", _jsx(_components.h3, {
        id: "sync-mode",
        children: "Sync mode"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports ", _jsx(_components.strong, {
          children: "Upsert"
        }), " mode using the ", _jsx(_components.a, {
          href: "https://docs.snowflake.com/en/sql-reference/sql/merge",
          children: _jsx(_components.code, {
            children: "MERGE INTO"
          })
        }), " statement, with the option to ", _jsx(_components.a, {
          href: "#delete-behavior",
          children: "delete removed rows"
        }), ", ", _jsx(_components.strong, {
          children: "Insert"
        }), " mode using the ", _jsx(_components.a, {
          href: "https://docs.snowflake.com/en/sql-reference/sql/copy-into-table",
          children: _jsx(_components.code, {
            children: "COPY INTO"
          })
        }), " statement, and ", _jsx(_components.strong, {
          children: "Mirror"
        }), " mode which will truncate the table and then insert all rows from the model."]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "Mirror"
          }), " mode will remove any existing data in the table before inserting all rows from the model"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "To match rows from your model to rows in the Iceberg table, Hightouch requires you to select a unique identifier in the table you are syncing to."
      }), "\n", _jsxs(_components.p, {
        children: ["The Snowflake column you select must be set as a ", _jsx(_components.code, {
          children: "UNIQUE"
        }), ", ", _jsx(_components.code, {
          children: "PRIMARY KEY"
        }), " column within Snowflake.\nYou can see columns that fit this criteria as available options in records matching section.\nIf there are no fields in the dropdown, you must add a unique type column to your Iceberg table. Then, click the refresh icon to access the newly created column."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["In ", _jsx(_components.strong, {
            children: "Insert"
          }), " mode, Snowflake automatically generates an identifier for every new record synced, so there is no need to match an existing record."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "column-types",
        children: "Column types"
      }), "\n", _jsx(_components.p, {
        children: "Iceberg tables have some data type limitations compared to standard Snowflake tables:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Supported Types"
            }), _jsx(_components.th, {
              children: "Notes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Text (VARCHAR)"
            }), _jsx(_components.td, {
              children: "Standard text/string data"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Number"
            }), _jsx(_components.td, {
              children: "INTEGER, FLOAT, NUMBER"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Boolean"
            }), _jsx(_components.td, {
              children: "TRUE/FALSE values"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Date"
            }), _jsx(_components.td, {
              children: "DATE type"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Timestamp (NTZ)"
            }), _jsx(_components.td, {
              children: "Timestamp without timezone"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Timestamp (LTZ)"
            }), _jsx(_components.td, {
              children: "Timestamp with local timezone (recommended for Iceberg)"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Time"
            }), _jsx(_components.td, {
              children: "TIME type"
            })]
          })]
        })]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "Semi-structured data types are not supported"
          }), " by Iceberg tables. This includes ", _jsx(_components.code, {
            children: "VARIANT"
          }), ", ", _jsx(_components.code, {
            children: "OBJECT"
          }), ", and ", _jsx(_components.code, {
            children: "ARRAY"
          }), " types. If your source data contains these types, you must transform them before syncing."]
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: [_jsx(_components.code, {
            children: "TIMESTAMP_LTZ"
          }), " is the recommended timestamp type for Iceberg tables. It internally stores UTC time with a specified precision. All operations are performed in the current session's time zone, controlled by the ", _jsx(_components.a, {
            href: "https://docs.snowflake.com/en/sql-reference/parameters.html#label-timezone",
            children: "TIMEZONE"
          }), " session parameter. Note that ", _jsx(_components.code, {
            children: "TIMESTAMP_TZ"
          }), " is not supported by Iceberg tables."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If you see type errors, it may be because your model is producing the wrong format. If so, use ", _jsx(_components.a, {
          href: "/models/data-types-casting",
          children: "typecasting"
        }), " or ", _jsx(_components.a, {
          href: "https://hightouch.com/docs/syncs/mapping-data#liquid-template-format",
          children: "liquid templating"
        }), " to resolve the issue."]
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your model to the columns in your Iceberg table. When auto-create is enabled, you can create new columns by mapping to column names that don't exist yet."
      }), "\n", _jsx(_components.h3, {
        id: "batch-size",
        children: "Batch size"
      }), "\n", _jsx(_components.p, {
        children: "You can tune the number of rows Hightouch upserts or inserts per query based on your needs and database threshold. The default is 50,000 rows per batch."
      }), "\n", _jsx(_components.p, {
        children: "If you want to increase the sync's speed, you can increase the batch size. Keep in mind that in upsert mode, Snowflake fails the entire batch of rows if it detects any erroneous row. If you suspect that you will have many bad rows, don't use a high batch size. To avoid locks, ensure you account for your database's capacity when increasing the batch size."
      }), "\n", _jsx(_components.h3, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "/syncs/overview#delete-behavior",
          children: "delete behavior"
        }), " you select dictates what to do when a row no longer appears in your model's query results. You have the following options:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the row in the Iceberg table"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete row"
              })
            }), _jsx(_components.td, {
              children: "Remove the row from the Iceberg table entirely"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "external-volume-errors",
        children: "External volume errors"
      }), "\n", _jsx(_components.p, {
        children: "If you see errors related to the external volume during connection testing, verify:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "The external volume exists in your Snowflake account"
        }), "\n", _jsxs(_components.li, {
          children: ["Your user has ", _jsx(_components.code, {
            children: "USAGE"
          }), " privilege on the external volume"]
        }), "\n", _jsx(_components.li, {
          children: "The external volume name is spelled correctly in the destination configuration"
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
